<template>
  <div class="details-wrapper">
    <div class="details-header">
      <button @click="emit('closeDetails')" class="return-options">
        <ArrowBackIcon /> Return
      </button>
      <span class="details__title">
        {{ title }}
      </span>
      <button @click="toggleInCart"><icon />
        {{ inCart ? 'Remove' : 'Add' }}
      </button>
    </div>
    <OverlayScrollbars :options="scrollbarOptions"
      class="results-scroller" 
      ref="resultsScrollbar">
      <VoyagerCard
        :result="props.result"
        :orientation="'horizontal'"
        class="summary-card">
        <template #header>
          <div></div>
        </template>
        <template #tools>
          <div></div>
        </template>
      </VoyagerCard>
      <VoyagerDetailsTable
        :result="props.result"
        :headers="tableHeaders"
        class="details-table" />
    </OverlayScrollbars>

  </div>
</template>

<script setup lang="ts">
import { VoyagerDetailsTable, VoyagerCard, VoyagerResult } from 'VoyagerComponents';
import { computed, ref, PropType } from 'vue';
import { ArrowBackIcon, MinusIcon, PlusIcon } from 'vue-tabler-icons';
import { useResultsStore } from '@/store/results';
import { useCartStore } from '@/store/cart';

const props = defineProps({
  result: {
    type: Object as PropType<VoyagerResult>,
    required: false
  },
  isInCart: {
    type: Boolean,
    default: false
  }
});

const { getFieldByDisplayKey } = useResultsStore(),
  { isItemInCart } = useCartStore(),
  icon = computed(() => inCart.value ? MinusIcon : PlusIcon),
  inCart = computed(() => isItemInCart(props.result?.id || 'foo').value),
  toggleInCart = () => {
      inCart.value ? 
        emit('userAction', { action: 'removeFromList', result: props.result }) : 
        emit('userAction', { action: 'addToList', result: props.result });
    }

const emit = defineEmits(['closeDetails', 'userAction']),
  title = computed(() => {
    if(!props.result) return;
    const field = getFieldByDisplayKey({ displayKey: 'header', result: props.result });
    if(!field) return '';
    if(Array.isArray(field)) return field[0].valueDisplay || field[0].value || '';
    return field?.valueDisplay || field?.value || '';
  }),
  tableHeaders = ref([
    {field: "name", fieldDisplay: "Name"},
    {field: "format", fieldDisplay: "Format"},
    {field: "id", fieldDisplay: "ID"},
    {field: "description", fieldDisplay: "Description"},
    {field: "keywords", fieldDisplay: "Keywords"},
    {field: "subject", fieldDisplay: "Subject"},
    {field: "ags_fused_cache", fieldDisplay: "ArcGIS Server Fused Cache"},
    {field: "format_type", fieldDisplay: "Format Type"},
    {field: "format_category", fieldDisplay: "Format Category"},
    {field: "path_to_thumb", fieldDisplay: "Path to Thumbnail"},
    {field: "srs", fieldDisplay: "Spatial Reference System"},
    {field: "bbox", fieldDisplay: "Bounding Box"},
    {field: "title", fieldDisplay: "Title"},
    {field: "linkcount__children", fieldDisplay: "Link Count"},
    {field: "fullpath", fieldDisplay: "Full Path"},
    {field: "absolute_path", fieldDisplay: "Absolute Path"},
    {field: "thumb", fieldDisplay: "Thumbnail"},
    {field: "download", fieldDisplay: "Download Link"},
    {field: "shard", fieldDisplay: "Shard"},
    {field: "geo", fieldDisplay: "Geographic Extent"}
  ]),
  scrollbarOptions = {
    scrollbars: {
      theme: 'os-theme-light',
    },
  };

</script>

<style lang="scss">
.details-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.results-scroller {
  flex-grow: 1;
}

.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #ccc;
  .details__title {
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-bold);
  }
  > button {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: .5rem;
    background: none;
    color: var(--color-text-primary);
    border: none;
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-bold);
    border-radius: var(--border-radius);
    &:hover {
      cursor: pointer;
      background-color: var(--color-action-background-primary);
      color: var(--color-action-text-primary)
    }
  }
}

.details-table {
  padding-top: 1rem;
  text-align: left;
  margin: 1rem;
  [data-theme=dark].details-table__row {
    --color-action-background-primary: white;
  }
}

.summary-card {
  height: auto;
  max-height: 25vh;
  border-radius: 0px;
  margin: 1rem;
  padding-bottom: 0.5rem;
  .result-card__header,.result-card__body {
    flex-grow: 1;
  }

  .result-card__thumbnail {
    width: 20%;
    img {
      width: 100%;
    }
  }
}
</style>